import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { Users, MessageSquare, ChevronRight, TrendingUp, TrendingDown } from 'lucide-react';
import GraficoResultadosGenerico from './GraficoResultadosGenerico';
import VencedorDestaqueGenerico from './VencedorDestaqueGenerico';
import VencedorDestaqueGrande from './VencedorDestaqueGrande';
import { celebrarVoto } from '../../utils/celebrarConfete';
import { useEhMobile } from '../../hooks/useEhMobile';
import CardEmpilhavelScroll from '../common/CardEmpilhavelScroll';

// Nuvem de palavras simples: tamanho da fonte proporcional à frequência.
// Mesma ideia visual da nuvem já usada em /votos-visual, mas alimentada
// pelos pares {palavra,total} que o backend já agrega — nunca a lista de
// respostas individuais (essa só existe no endpoint admin).
const NuvemPalavras = ({ palavras }) => {
  if (!palavras || palavras.length === 0) {
    return <p className="text-sm text-gray-400">Nenhuma palavra registrada ainda.</p>;
  }

  const max = palavras[0].total;
  const min = palavras[palavras.length - 1].total;

  return (
    <div className="leading-relaxed">
      {palavras.map(({ palavra, total }) => {
        const tamanho = min === max ? 16 : Math.round(12 + ((total - min) / (max - min)) * 22);
        return (
          <span key={palavra} style={{ fontSize: `${tamanho}px` }} className="inline-block mr-3 mb-1 text-primary-700">
            {palavra} <small className="text-gray-400 text-xs">({total})</small>
          </span>
        );
      })}
    </div>
  );
};

// Card grande do troféu (VencedorDestaqueGrande) é reservado pra enquete de
// UMA pergunta só — múltipla escolha OU única escolha com exatamente 2
// opções, nunca aberta. Com 2+ perguntas na mesma enquete, declarar
// "vencedor" pergunta por pergunta destacaria demais um resultado parcial
// dentro de um conjunto maior — nesse caso o resumo compacto de sempre
// (VencedorDestaqueGenerico) é usado pra toda pergunta, sem exceção.
// Compartilhada com o efeito de confete em ResultadoEnqueteDinamica logo
// abaixo, pra não correr o risco das duas regras divergirem.
function elegivelParaTrofeu(pergunta, totalPerguntas) {
  if (totalPerguntas !== 1 || pergunta.tipo === 'aberta') return false;
  return pergunta.tipo === 'multipla_escolha' || (pergunta.itens || []).length === 2;
}

// Indicadores extras pro card grande de troféu — inspirado na grade
// EstatisticasGerais.js da tela legada do Telão Brasil, mas só com o que
// ainda NÃO aparece em nenhum outro lugar da tela: Total de Respondentes
// ("X pessoa(s) já responderam") e Encerramento ("Encerrado em...") já
// aparecem acima, em FormularioDinamico.js — repetir aqui seria redundante,
// por isso ficam de fora. Margem e lanterna só fazem sentido com 2+ opções
// com voto de verdade pra comparar.
const IndicadoresExtras = ({ itens, totalRespondentes }) => {
  const ordenados = [...(itens || [])].sort((a, b) => b.votos - a.votos);
  if (ordenados.length < 2) return null;

  const percentualDe = (votos) => (totalRespondentes > 0 ? (votos / totalRespondentes) * 100 : 0);
  const margem = percentualDe(ordenados[0].votos) - percentualDe(ordenados[1].votos);
  const lanterna = ordenados[ordenados.length - 1];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
      <div className="bg-white rounded-xl shadow-md border border-gray-100 p-5">
        <div className="bg-yellow-50 w-10 h-10 rounded-full flex items-center justify-center mb-3">
          <TrendingUp className="w-5 h-5 text-yellow-600" />
        </div>
        <p className="text-sm text-gray-500 mb-1">Margem pro 2º colocado</p>
        <p className="text-xl font-bold text-gray-900">{margem.toFixed(1)} pontos %</p>
      </div>

      <div className="bg-white rounded-xl shadow-md border border-gray-100 p-5">
        <div className="bg-gray-100 w-10 h-10 rounded-full flex items-center justify-center mb-3">
          <TrendingDown className="w-5 h-5 text-gray-500" />
        </div>
        <p className="text-sm text-gray-500 mb-1">Opção menos votada</p>
        <p className="text-xl font-bold text-gray-900 truncate">{lanterna.label}</p>
        <p className="text-sm text-gray-500">{lanterna.votos.toLocaleString('pt-BR')} votos ({percentualDe(lanterna.votos).toFixed(1)}%)</p>
      </div>
    </div>
  );
};

// Mesmo sendo elegível, o troféu (com o par de indicadores votos/porcentagem
// embutido nele) só aparece depois que a enquete encerra de verdade —
// enquanto ainda está sendo votada, "vencedor" seria prematuro, então só o
// gráfico de barras deitado com os números atuais aparece nesse meio-tempo
// (quando modo_resultado='tempo_real' permite mostrar algo).
const CardPergunta = ({ pergunta, totalRespondentes, statusEnquete, modoResultado, totalPerguntas }) => {
  if (pergunta.tipo === 'aberta') {
    return (
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-primary-600" /> {pergunta.texto}
        </h3>
        <p className="text-sm text-gray-500 mb-3">{pergunta.totalRespostas} resposta(s)</p>
        <NuvemPalavras palavras={pergunta.nuvemPalavras || []} />
      </div>
    );
  }

  const vencedor = [...(pergunta.itens || [])].sort((a, b) => b.votos - a.votos)[0];
  const elegivel = elegivelParaTrofeu(pergunta, totalPerguntas);
  const encerrada = statusEnquete === 'encerrada';
  const mostrarTrofeu = elegivel && encerrada;

  let destaque;
  if (mostrarTrofeu) {
    destaque = <VencedorDestaqueGrande vencedor={vencedor} totalRespondentes={totalRespondentes} itens={pergunta.itens} />;
  } else if (elegivel && !encerrada && modoResultado === 'tempo_real') {
    destaque = null; // só o gráfico embaixo, sem declarar vencedor ainda
  } else {
    destaque = <VencedorDestaqueGenerico vencedor={vencedor} totalRespondentes={totalRespondentes} />;
  }

  return (
    <div className="card">
      {mostrarTrofeu ? (
        // Cabeçalho grande de "resultado final" — inspirado na tela legada
        // do Telão do Brasil (ResultadoEnquete/index.js: título
        // text-3xl/4xl font-black acima do card do vencedor), mas
        // ADAPTATIVO: usa o texto real da própria pergunta em vez de copy
        // fixo tipo "Resultado Final - Telão do Brasil" — funciona pra
        // qualquer enquete de pergunta única elegível, sem configuração
        // nenhuma extra no admin. Só entra nesse caminho (troféu grande já
        // é hero o bastante); nos outros dois casos o h3 modesto de sempre.
        <div className="text-center mb-6">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold tracking-wide uppercase text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-full px-3 py-1 mb-3">
            🏆 Resultado final
          </span>
          <h3 className="text-2xl md:text-3xl font-black text-gray-900">{pergunta.texto}</h3>
        </div>
      ) : (
        <h3 className="text-lg font-semibold text-gray-900 mb-3">{pergunta.texto}</h3>
      )}
      {destaque}
      <GraficoResultadosGenerico itens={pergunta.itens} totalRespondentes={totalRespondentes} />
      {mostrarTrofeu && <IndicadoresExtras itens={pergunta.itens} totalRespondentes={totalRespondentes} />}
    </div>
  );
};

const LIMITE_ARRASTE_PX = 120;
const LIMITE_VELOCIDADE = 500;
const CARTAS_VISIVEIS_NA_PILHA = 3;

// Uma carta da pilha (posicao 0 = topo, a única arrastável de verdade —
// as de trás só reagem visualmente, sem gesto próprio). `x`/`rotate` viram
// motion values de verdade só na carta do topo; nas de trás ficam em 0
// fixo, sem custo de gesto nenhum. `medirRef` (só passado pra posicao 0)
// é como PilhaPerguntas mede a altura real do conteúdo pra dar altura ao
// container — todas as cartas são position:absolute (pra empilhar de
// verdade), então nenhuma contribui pro fluxo normal do documento sozinha.
const CartaArrastavel = ({ pergunta, posicao, ehTopo, medirRef, onDescartar, ...propsCard }) => {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-15, 15]);
  const [direcaoSaida, setDirecaoSaida] = useState(1);

  const finalizarArraste = (_evento, info) => {
    const passouLimite = Math.abs(info.offset.x) > LIMITE_ARRASTE_PX || Math.abs(info.velocity.x) > LIMITE_VELOCIDADE;
    if (!passouLimite) return; // dragConstraints já cuida de voltar pro centro
    setDirecaoSaida(info.offset.x > 0 ? 1 : -1);
    onDescartar();
  };

  return (
    <motion.div
      ref={medirRef}
      className="absolute inset-x-0 top-0"
      style={{ zIndex: 10 - posicao, x: ehTopo ? x : 0, rotate: ehTopo ? rotate : 0 }}
      animate={{ scale: 1 - posicao * 0.04, y: posicao * 12, opacity: posicao >= 2 ? 0.6 : 1 }}
      exit={{ x: direcaoSaida * 500, opacity: 0, transition: { duration: 0.25 } }}
      transition={{ type: 'spring', stiffness: 300, damping: 28 }}
      drag={ehTopo ? 'x' : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.7}
      whileDrag={{ cursor: 'grabbing' }}
      onDragEnd={ehTopo ? finalizarArraste : undefined}
    >
      <CardPergunta pergunta={pergunta} {...propsCard} />
    </motion.div>
  );
};

// Pilha estilo Tinder pra enquete de VÁRIAS perguntas no MOBILE — arrasta o
// card do topo pra qualquer lado pra descartar e revelar a próxima pergunta
// (troféu grande nunca aparece aqui, `elegivelParaTrofeu` já bloqueia isso
// quando totalPerguntas > 1; toda pergunta cai no VencedorDestaqueGenerico).
// Desktop usa PilhaRolagem logo abaixo — arraste não é o gesto natural de
// mouse. Com 1 pergunta só, ResultadoEnqueteDinamica desenha o CardPergunta
// direto, sem pilha nenhuma (não faz sentido arrastar pra "revelar" um card
// que não tem nada atrás).
// `corTexto` (ver corTextoContraste.js) — mesma herança do "Encerrado em..."
// em FormularioDinamico.js — é o que mantém o texto solto abaixo legível
// contra qualquer fundo personalizado escolhido no admin; sem ele esse texto
// ficava sempre cinza-claro fixo, quase invisível em fundo escuro/colorido.
const PilhaPerguntas = ({ perguntas, corTexto, ...propsCard }) => {
  const [indice, setIndice] = useState(0);
  const [alturaTopo, setAlturaTopo] = useState(null);
  const refTopo = useRef(null);

  useLayoutEffect(() => {
    if (refTopo.current) setAlturaTopo(refTopo.current.offsetHeight);
  }, [indice]);

  if (indice >= perguntas.length) {
    return (
      <div className="card text-center py-10 animate-fade-in-up">
        <p className="text-gray-600 font-medium mb-4">Você viu o resultado de todas as perguntas.</p>
        <button type="button" onClick={() => setIndice(0)} className="btn-primary">
          Ver novamente
        </button>
      </div>
    );
  }

  const avancar = () => setIndice((i) => i + 1);
  const pilhaVisivel = perguntas.slice(indice, indice + CARTAS_VISIVEIS_NA_PILHA);

  return (
    <div>
      <p className={`text-center text-xs font-medium mb-2 ${corTexto?.corpo || 'text-gray-500'}`}>
        Pergunta {indice + 1} de {perguntas.length} — arraste o card para o lado
      </p>
      <div
        className="relative transition-[height] duration-300"
        style={{ height: alturaTopo ? `${alturaTopo}px` : 'auto' }}
      >
        <AnimatePresence>
          {pilhaVisivel.map((pergunta, posicao) => (
            <CartaArrastavel
              key={pergunta.id}
              pergunta={pergunta}
              posicao={posicao}
              ehTopo={posicao === 0}
              medirRef={posicao === 0 ? refTopo : undefined}
              onDescartar={avancar}
              {...propsCard}
            />
          ))}
        </AnimatePresence>
      </div>
      <div className="flex justify-center mt-4">
        <button
          type="button"
          onClick={avancar}
          className={`inline-flex items-center gap-1 text-sm hover:text-primary-500 transition-colors ${corTexto?.corpo || 'text-gray-500'}`}
        >
          Próxima pergunta <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Versão desktop da pilha — todas as perguntas ficam no DOM (sem
// arraste/descarte, mouse não tem swipe natural), o empilhar/desempilhar é
// só rolagem: cada CardEmpilhavelScroll (ver components/common/) gruda no
// topo até o próximo cobrir ele.
const PilhaRolagem = ({ perguntas, ...propsCard }) => (
  <div className="relative">
    {perguntas.map((pergunta, indice) => (
      <CardEmpilhavelScroll key={pergunta.id} indice={indice}>
        <CardPergunta pergunta={pergunta} {...propsCard} />
      </CardEmpilhavelScroll>
    ))}
  </div>
);

// Orquestrador de resultado para enquetes dinâmicas — mostra o resultado de
// N perguntas de tipos variados. Sem "troféu geral" da enquete inteira
// (diferente do ResultadoEnquete/index.js original, feito pra 1 pergunta só):
// aqui cada pergunta mostra o próprio destaque, lado a lado.
// `corTexto` (ver corTextoContraste.js) só afeta o texto solto direto aqui
// fora de qualquer card — os CardPergunta abaixo continuam com cor fixa,
// já estão dentro de fundo branco.
const ResultadoEnqueteDinamica = ({ stats, titulo, corTexto }) => {
  const enquete = stats?.enquete;
  const perguntas = stats?.perguntas;
  const totalRespondentes = stats?.totalRespondentes;
  const ehMobile = useEhMobile();

  // Confete aqui é sobre revelar o resultado final, não sobre "obrigado por
  // votar" (esse já dispara à parte em FormularioDinamico.js, e de propósito
  // nunca quando a enquete está encerrada — não faz sentido comemorar
  // "acabei de votar" numa enquete fechada há tempos). Aqui só dispara com a
  // enquete ESTÁ encerrada e existe gente que respondeu, em dois formatos:
  // pergunta única elegível pro troféu (mesma regra de elegivelParaTrofeu
  // acima, com voto de verdade nela) OU 2+ perguntas — nesse caso não tem
  // troféu nenhum pra revelar, mas o confete geral ainda comemora o
  // resultado final chegando na tela. Precisa ficar antes do "if (!stats)
  // return null" abaixo — hook não pode vir depois de um retorno condicional.
  useEffect(() => {
    if (!enquete || enquete.status !== 'encerrada' || !perguntas || perguntas.length === 0) return;

    if (perguntas.length > 1) {
      if (totalRespondentes > 0) celebrarVoto();
      return;
    }

    const temVencedorEmDestaque = perguntas.some((p) =>
      elegivelParaTrofeu(p, perguntas.length) && (p.itens || []).some((i) => i.votos > 0)
    );
    if (temVencedorEmDestaque) celebrarVoto();
  }, [enquete, perguntas, totalRespondentes]);

  if (!stats) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className={`flex items-center justify-center gap-2 mb-2 ${corTexto?.corpo || 'text-gray-600'}`}>
        <Users className="w-5 h-5" />
        <span className="font-medium">{totalRespondentes} pessoa(s) já responderam{titulo ? ` — ${titulo}` : ''}</span>
      </div>

      {(!perguntas || perguntas.length === 0) && (
        <p className={`text-center py-8 ${corTexto?.corpo || 'text-gray-500'}`}>Nenhum resultado disponível ainda.</p>
      )}

      {perguntas?.length === 1 && (
        <CardPergunta
          pergunta={perguntas[0]}
          totalRespondentes={totalRespondentes}
          statusEnquete={enquete?.status}
          modoResultado={enquete?.modo_resultado}
          totalPerguntas={1}
        />
      )}

      {perguntas?.length > 1 && (
        ehMobile ? (
          <PilhaPerguntas
            perguntas={perguntas}
            corTexto={corTexto}
            totalRespondentes={totalRespondentes}
            statusEnquete={enquete?.status}
            modoResultado={enquete?.modo_resultado}
            totalPerguntas={perguntas.length}
          />
        ) : (
          <PilhaRolagem
            perguntas={perguntas}
            totalRespondentes={totalRespondentes}
            statusEnquete={enquete?.status}
            modoResultado={enquete?.modo_resultado}
            totalPerguntas={perguntas.length}
          />
        )
      )}
    </div>
  );
};

export default ResultadoEnqueteDinamica;
